import { useState, useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { useStockAdjustmentStore, useProductStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Input, Select } from '@lancar-pos/ui';

const adjustmentTypes = [
  { value: 'increase', label: 'Penambahan' },
  { value: 'decrease', label: 'Pengurangan' },
];

const reasons = [
  { value: 'stock_opname', label: 'Stock Opname' },
  { value: 'damage', label: 'Kerusakan' },
  { value: 'loss', label: 'Hilang' },
  { value: 'found', label: 'Ditemukan' },
  { value: 'correction', label: 'Koreksi' },
  { value: 'other', label: 'Lainnya' },
];

export function StockAdjustmentFormPage() {
  const navigate = useNavigate();
  const { adjustmentId } = useParams({ from: '/app/inventory/stock-adjustments/$adjustmentId' });
  const isNew = adjustmentId === 'new';
  
  const { addStockAdjustment, approveAdjustment, rejectAdjustment, getStockAdjustmentById } = useStockAdjustmentStore();
  const { products, getProductById } = useProductStore();
  const toast = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    productId: '',
    adjustmentType: 'increase' as 'increase' | 'decrease',
    quantity: 0,
    reason: 'stock_opname',
    notes: '',
  });

  const existingAdjustment = useMemo(() => {
    if (!isNew && adjustmentId) {
      return getStockAdjustmentById(adjustmentId);
    }
    return null;
  }, [isNew, adjustmentId, getStockAdjustmentById]);

  const productOptions = useMemo(() => {
    return products
      .filter((p) => !p._deleted && p.isActive)
      .map((p) => ({ value: p.id, label: `${p.name} (Stok: ${p.stock})` }));
  }, [products]);

  const selectedProduct = useMemo(() => {
    if (formData.productId) {
      return getProductById(formData.productId);
    }
    return null;
  }, [formData.productId, getProductById]);

  const canSubmit = formData.productId && formData.quantity > 0 && formData.reason;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit) {
      toast.error('Mohon lengkapi form dengan benar');
      return;
    }

    if (formData.adjustmentType === 'decrease' && selectedProduct && selectedProduct.stock < formData.quantity) {
      toast.error('Stok tidak mencukupi');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await addStockAdjustment({
        productId: formData.productId,
        adjustmentType: formData.adjustmentType,
        quantity: formData.quantity,
        reason: formData.reason as 'stock_opname' | 'damage' | 'loss' | 'found' | 'correction' | 'other',
        notes: formData.notes,
        approvedBy: '',
        status: 'pending',
        createdBy: '',
      });
      toast.success('Penyesuaian stok berhasil dibuat');
      navigate({ to: '/app/inventory/stock-adjustments' });
    } catch (error) {
      toast.error('Gagal membuat penyesuaian stok');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!existingAdjustment) return;
    
    try {
      await approveAdjustment(existingAdjustment.id, 'admin');
      toast.success('Penyesuaian disetujui');
      navigate({ to: '/app/inventory/stock-adjustments' });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Gagal menyetujui';
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    if (!existingAdjustment) return;
    
    try {
      await rejectAdjustment(existingAdjustment.id, 'admin');
      toast.success('Penyesuaian ditolak');
      navigate({ to: '/app/inventory/stock-adjustments' });
    } catch (error) {
      toast.error('Gagal menolak');
    }
  };

  if (!isNew && !existingAdjustment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Penyesuaian tidak ditemukan</p>
      </div>
    );
  }

  if (!isNew && existingAdjustment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate({ to: '/app/inventory/stock-adjustments' })}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                Detail Penyesuaian Stok
              </h1>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto p-4 lg:p-6 space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Produk:</span>
                  <p className="font-medium">{selectedProduct?.name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Stok Saat Ini:</span>
                  <p className="font-medium">{selectedProduct?.stock || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">Tipe:</span>
                  <p className={`font-medium ${existingAdjustment.adjustmentType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                    {existingAdjustment.adjustmentType === 'increase' ? 'Penambahan' : 'Pengurangan'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Jumlah:</span>
                  <p className="font-medium">{existingAdjustment.quantity}</p>
                </div>
                <div>
                  <span className="text-gray-500">Alasan:</span>
                  <p className="font-medium">{reasons.find(r => r.value === existingAdjustment.reason)?.label || existingAdjustment.reason}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <p className="font-medium">{existingAdjustment.status}</p>
                </div>
                {existingAdjustment.notes && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Catatan:</span>
                    <p className="font-medium">{existingAdjustment.notes}</p>
                  </div>
                )}
              </div>

              {existingAdjustment.status === 'pending' && (
                <div className="mt-6 flex gap-3">
                  <Button onClick={handleApprove}>Setuju</Button>
                  <Button variant="danger" onClick={handleReject}>Tolak</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app/inventory/stock-adjustments' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Penyesuaian Stok Baru
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 lg:p-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent className="p-6 space-y-4">
              <Select
                label="Produk"
                placeholder="Pilih produk"
                value={formData.productId}
                onChange={(value: string) => setFormData({ ...formData, productId: value })}
                options={productOptions}
              />
              
              {selectedProduct && (
                <div className="text-sm text-gray-600">
                  Stok saat ini: <span className="font-semibold">{selectedProduct.stock}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Tipe Penyesuaian</label>
                <div className="flex gap-4">
                  {adjustmentTypes.map((type) => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="adjustmentType"
                        value={type.value}
                        checked={formData.adjustmentType === type.value}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          adjustmentType: e.target.value as 'increase' | 'decrease' 
                        })}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <Input
                label="Jumlah"
                type="number"
                value={formData.quantity.toString()}
                onChange={(value: string) => setFormData({ ...formData, quantity: parseInt(value) || 0 })}
                placeholder="0"
              />
              
              <Select
                label="Alasan"
                placeholder="Pilih alasan"
                value={formData.reason}
                onChange={(value: string) => setFormData({ ...formData, reason: value })}
                options={reasons}
              />
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Catatan</label>
                <textarea
                  value={formData.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Catatan tambahan"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => navigate({ to: '/app/inventory/stock-adjustments' })}
                >
                  Batal
                </Button>
                <Button type="submit" disabled={isSubmitting || !canSubmit}>
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
}
